/**
 * reCAPTCHA v3 utility for Next.js
 */

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

let scriptLoaded = false;

/**
 * Load the reCAPTCHA script if not already loaded
 */
export function loadRecaptchaScript(): Promise<void> {
  return new Promise((resolve) => {
    if (!SITE_KEY) {
      console.warn("[reCAPTCHA] No site key configured");
      resolve();
      return;
    }

    if (scriptLoaded || typeof window === "undefined") {
      resolve();
      return;
    }

    // Check if already loaded
    if (window.grecaptcha) {
      scriptLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      scriptLoaded = true;
      resolve();
    };
    document.head.appendChild(script);
  });
}

/**
 * Execute reCAPTCHA and get a token
 */
export async function executeRecaptcha(action: string): Promise<string | null> {
  if (!SITE_KEY) {
    console.warn("[reCAPTCHA] No site key configured, skipping");
    return null;
  }

  try {
    await loadRecaptchaScript();
    
    return new Promise((resolve) => {
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(SITE_KEY, { action });
          resolve(token);
        } catch (error) {
          console.error("[reCAPTCHA] Execute error:", error);
          resolve(null);
        }
      });
    });
  } catch (error) {
    console.error("[reCAPTCHA] Error:", error);
    return null;
  }
}
